import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ReplenishDialog from '../components/ReplenishDialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { MagnifyingGlass, MapPin, Package, Tag, Warning, QrCode, ArrowsLeftRight, PushPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  { key: 'location', label: 'Location', icon: MapPin, placeholder: 'Enter location code or scan barcode…' },
  { key: 'item',     label: 'Item',     icon: Package, placeholder: 'Enter SKU code or scan barcode…' },
];

export default function Lookup() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'item' ? 'item' : 'location');
  const [q, setQ] = useState(params.get('q') || '');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [tab]);

  // Auto-run lookup when the page is opened with ?q= (e.g. arriving from /lookup/scan).
  // Fires once on mount only; subsequent searches are triggered by the form submit.
  useEffect(() => {
    const initialQ = params.get('q');
    if (initialQ && initialQ.trim()) {
      // Defer one tick so state (tab, q) is settled before the async fetch.
      setTimeout(() => { run(); }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    setBusy(true); setErr(null); setData(null);
    setParams({ tab, q: query }, { replace: true });
    try {
      const url = tab === 'location'
        ? `${API}/api/lookups/location/${encodeURIComponent(query)}`
        : `${API}/api/lookups/item/${encodeURIComponent(query)}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (k) => { setTab(k); setData(null); setErr(null); setQ(''); setParams({ tab: k }, { replace: true }); };

  return (
    <Layout>
      <div className="p-6 max-w-5xl" data-testid="lookup-page">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <MagnifyingGlass size={30} weight="bold" className="text-brand" />
              Stock Lookup
            </h1>
            <p className="text-muted-foreground text-sm">Find what's where in seconds — scan a location or paste a SKU</p>
          </div>
          <Button
            data-testid="lookup-scan-mode"
            onClick={() => window.location.assign('/lookup/scan')}
            className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
          >
            <QrCode size={16} weight="bold" className="mr-2" />
            Scan mode
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4" data-testid="lookup-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`lookup-tab-${t.key}`}
                onClick={() => switchTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 transition ${
                  active ? 'border-brand text-brand' : 'border-transparent text-muted-foreground hover:text-slate-800'
                }`}
              >
                <Icon size={14} weight="bold" />{t.label} lookup
              </button>
            );
          })}
        </div>

        {/* Search form */}
        <form onSubmit={run} className="flex gap-2 mb-6">
          <Input
            ref={inputRef}
            data-testid="lookup-input"
            placeholder={TABS.find((t) => t.key === tab).placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="text-base font-mono uppercase"
            autoFocus
          />
          <Button data-testid="lookup-submit" type="submit" disabled={busy || !q.trim()} className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold">
            {busy ? 'Searching…' : 'Search'}
          </Button>
        </form>

        {err && (
          <div data-testid="lookup-error" className="p-4 border border-rose-200 bg-rose-50 text-rose-700 text-sm flex items-center gap-2 mb-4">
            <Warning weight="bold" size={16} />{err}
          </div>
        )}

        {data && tab === 'location' && <LocationResult data={data} onReplenished={run} />}
        {data && tab === 'item' && <ItemResult data={data} />}

        {!data && !err && !busy && (
          <div className="text-center text-muted-foreground font-mono text-xs uppercase tracking-widest py-12 border border-dashed border-border">
            Type {tab === 'location' ? 'a location code' : 'a SKU code'} above and hit Search
          </div>
        )}
      </div>
    </Layout>
  );
}

// ===== LOCATION RESULT =====
function LocationResult({ data, onReplenished }) {
  const { location, items, total_skus, total_units_on_hand } = data;
  const [replenish, setReplenish] = useState(null); // {row, location}
  const navigate = useNavigate();
  const fixRule = (row) => {
    const params = new URLSearchParams({
      create: '1',
      sku_id: row.sku_id || '',
      variant_id: row.variant_id || '',
      location_id: location.id,
    });
    navigate(`/fixed-locations?${params.toString()}`);
  };
  return (
    <div data-testid="lookup-location-result" className="space-y-4">
      {/* Header card */}
      <div className="bg-card border border-brand p-5">
        <div className="flex items-baseline gap-3">
          <MapPin size={22} weight="fill" className="text-brand shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Location</div>
            <div className="text-2xl font-bold font-mono">{location.location_code}</div>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">
              Zone {location.zone || '—'} · Barcode {location.barcode || '—'} · Capacity {location.capacity ?? '∞'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Items</div>
            <div className="text-xl font-bold">{total_skus}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 font-bold">Units on hand</div>
            <div className="text-xl font-bold">{total_units_on_hand}</div>
          </div>
        </div>
      </div>
      <Table
        testid="lookup-location-items"
        headers={['SKU', 'Name', 'Variant', 'UOM', 'On-hand available', 'On-hand reserved', 'Net available', '']}
        rows={items.map((i) => ({
          key: i.variant_id,
          testid: `loc-item-${i.sku_code}`,
          cells: [
            <span className="font-mono font-bold">{i.sku_code || '—'}</span>,
            i.sku_name || '—',
            <AttrPill attrs={i.variant_attrs} barcode={i.variant_barcode} />,
            <span className="font-mono text-xs uppercase">{i.uom}</span>,
            <Qty value={i.on_hand_available} strong />,
            <Qty value={i.on_hand_reserved} color="text-amber-700" />,
            <Qty value={i.net_available} color="text-emerald-700" strong />,
            <div className="flex items-center gap-1 justify-end">
              <ReplenishTrigger row={i} onClick={() => setReplenish({ row: i, location })} />
              <FixRuleTrigger row={i} onClick={() => fixRule(i)} />
            </div>,
          ]
        }))}
        empty="No stock at this location"
      />
      {replenish && (
        <ReplenishDialog
          row={replenish.row}
          targetLocation={replenish.location}
          onClose={() => setReplenish(null)}
          onDone={() => { setReplenish(null); onReplenished?.(); }}
        />
      )}
    </div>
  );
}

// Shows a small "Replenish" button only when the item is running low at this
// location — net_available below a minimum OR fully tied up in reservations.
function ReplenishTrigger({ row, onClick }) {
  const low = (row.net_available ?? 0) < 5 || (row.on_hand_reserved ?? 0) >= (row.on_hand_available ?? 0);
  if (!low) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <Button
      data-testid={`replenish-trigger-${row.sku_code}`}
      size="sm"
      onClick={onClick}
      className="bg-amber-500 hover:bg-amber-600 text-white rounded-none font-bold text-[10px] uppercase tracking-widest"
    >
      <ArrowsLeftRight size={12} weight="bold" className="mr-1" />
      Replenish
    </Button>
  );
}

// "Pin SKU to this location" — deep-links into the Fixed Locations create
// dialog with sku_id + variant_id + location_id pre-filled. Shown on every
// row, disabled only when sku_id is unknown.
function FixRuleTrigger({ row, onClick }) {
  if (!row.sku_id) return null;
  return (
    <Button
      data-testid={`fix-rule-trigger-${row.sku_code}`}
      size="sm"
      variant="outline"
      onClick={onClick}
      title="Create a fixed-location rule for this SKU at this location"
      className="rounded-none h-7 text-[10px] uppercase tracking-widest border-brand text-brand hover:bg-brand hover:text-white"
    >
      <PushPin size={12} weight="fill" className="mr-1" />
      Pin
    </Button>
  );
}



// ===== ITEM RESULT =====
function ItemResult({ data }) {
  const { item, locations, total_locations, total_on_hand, total_reserved, net_available } = data;
  return (
    <div data-testid="lookup-item-result" className="space-y-4">
      <div className="bg-card border border-brand p-5">
        <div className="flex items-baseline gap-3">
          {item.image_base64 ? (
            <img
              src={item.image_base64}
              alt={item.sku_code}
              className="w-16 h-16 md:w-20 md:h-20 object-cover border border-border shrink-0 self-start"
              data-testid="lookup-item-image"
            />
          ) : (
            <Tag size={22} weight="fill" className="text-brand shrink-0" />
          )}
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Item</div>
            <div className="text-2xl font-bold font-mono">{item.sku_code}</div>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">
              {item.name} · {item.variants} variant{item.variants !== 1 ? 's' : ''} · {item.uom}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 text-right">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Locations</div>
              <div className="text-xl font-bold">{total_locations}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">On-hand</div>
              <div className="text-xl font-bold">{total_on_hand}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Net available</div>
              <div className="text-xl font-bold text-emerald-700">{net_available}</div>
            </div>
          </div>
        </div>
        {total_reserved > 0 && (
          <div className="mt-3 text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 inline-block">
            Reserved (global): {total_reserved}
          </div>
        )}
      </div>
      <Table
        testid="lookup-item-locations"
        headers={['Location', 'Zone', 'Variant', 'On-hand available', 'On-hand reserved', 'Net available']}
        rows={locations.map((r) => ({
          key: `${r.location_id}-${r.variant_id}`,
          testid: `item-loc-${r.location_code}`,
          cells: [
            <span className="font-mono font-bold">{r.location_code}</span>,
            r.zone || '—',
            <AttrPill attrs={r.variant_attrs} barcode={r.variant_barcode} />,
            <Qty value={r.on_hand_available} strong />,
            <Qty value={r.on_hand_reserved} color="text-amber-700" />,
            <Qty value={r.net_available} color="text-emerald-700" strong />,
          ]
        }))}
        empty="This item is not stocked in any location"
      />
    </div>
  );
}

// ===== helpers =====
const AttrPill = ({ attrs, barcode }) => {
  const parts = [attrs?.size, attrs?.color, attrs?.style].filter(Boolean);
  return (
    <div className="text-xs font-mono">
      {parts.length > 0 ? parts.join(' · ') : <span className="text-slate-300">—</span>}
      {barcode && <div className="text-[10px] text-muted-foreground uppercase">{barcode}</div>}
    </div>
  );
};

const Qty = ({ value, strong, color }) => (
  <span className={`font-mono ${strong ? 'font-bold' : ''} ${color || 'text-slate-800'}`}>{value ?? 0}</span>
);

const Table = ({ testid, headers, rows, empty }) => (
  <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-muted text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
        <tr>
          {headers.map((h, i) => <th key={i} className={`py-2 px-3 ${i >= headers.length - 3 ? 'text-right' : 'text-left'}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} className="py-8 text-center text-muted-foreground font-mono text-xs uppercase">{empty}</td></tr>
        ) : rows.map((r) => (
          <tr key={r.key} data-testid={r.testid} className="border-b border-border hover:bg-muted">
            {r.cells.map((c, i) => <td key={i} className={`py-2 px-3 ${i >= r.cells.length - 3 ? 'text-right' : 'text-left'}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
